import React from "react";
import { connect } from "react-redux";
import moment from "moment";

import language from "../../../language";
import { Input, Select } from "../../Input";
import { Button } from "../../Button";
import { Separator } from "../../Separator";
import { ConfirmationModal, Modal } from "../../Modal";
import toastMessage from "../../../utils/toastMessage";
import { getStorage } from "../../../utils/storage";
import { defineRole } from "../../../utils/handleAccessRoles";
import { ROLE_LABORATORY } from "../../../constants/ROLES";
import { fetchShippingInQueueData } from "../../../utils/queries/request/shipping-queue";
import {
  addEmergencyResults,
  recallEmergencyUnit,
} from "../../../utils/queries/emergency";

// Same phases the crossmatch panel uses, so a result entered late reads the
// same way as one entered in time.
const REACTIVITY = [
  { label: "Non-Reactive", value: "Non-Reactive" },
  { label: "Reactive", value: "Reactive" },
];

const CHECK_CELLS = [
  { label: "Positive", value: "Positive" },
  { label: "Negative", value: "Negative" },
];

const COMPATIBILITY = [
  { label: "Compatible", value: "compatible" },
  { label: "Incompatible", value: "incompatible" },
];

/**
 * The paper form's last two tables: what was issued, and what the tests said
 * once they were finally done.
 *
 * An emergency release puts units into a patient before the laboratory has
 * finished - sometimes before it has a sample at all. The results still have
 * to be recorded, and if one of them says the unit was incompatible the
 * doctor has to be told to stop while there is still something to stop.
 */
class PostReleaseResults extends React.Component {
  state = {
    issued: [],
    isLoading: false,
    isSubmitting: false,
    canRecord: false,
    patientBloodGroup: "",
    // { [donationNumber]: { bloodGroup, immediateSpin, ... } }
    rows: {},
    recalling: null,
    isRecalling: false,
  };

  componentDidMount = async () => {
    const user = await getStorage();

    this.setState({
      canRecord: defineRole({
        roles: user.accessRole,
        menu: ROLE_LABORATORY,
        operation: "create",
      }),
      patientBloodGroup:
        this.props.request?.emergencyRelease?.patientBloodGroup ||
        this.props.request?.sampleBloodGroup ||
        "",
    });

    this.getIssued();
  };

  getIssued = async () => {
    const { shipmentNumber } = this.props.request || {};

    if (!shipmentNumber) return;

    try {
      this.setState({ isLoading: true });

      const { data } = await fetchShippingInQueueData(
        this.props.defaultLanguage,
        { page: 1, limit: 100, shipmentNumber }
      );

      this.setState({ issued: data || [], isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false });
      toastMessage("error", error);
    }
  };

  // Results already recorded, keyed by the unit they belong to, so a unit that
  // has been answered shows its answer instead of an empty row.
  recorded = () => {
    const results = this.props.request?.emergencyRelease?.postReleaseResults || [];
    const byDonation = {};

    for (const el of results) byDonation[el.donationNumber] = el;

    return byDonation;
  };

  setCell = (donationNumber, field, value) => {
    this.setState((prev) => ({
      rows: {
        ...prev.rows,
        [donationNumber]: { ...(prev.rows[donationNumber] || {}), [field]: value },
      },
    }));
  };

  onSave = async () => {
    const results = Object.entries(this.state.rows)
      .filter(([, row]) => row.compatibility)
      .map(([donationNumber, row]) => ({ donationNumber, ...row }));

    if (!results.length) {
      toastMessage(
        "error",
        language[this.props.defaultLanguage].compatibility +
          ": " +
          language[this.props.defaultLanguage].required
      );
      return;
    }

    try {
      this.setState({ isSubmitting: true });

      await addEmergencyResults({
        id: this.props.request._id,
        patientBloodGroup: this.state.patientBloodGroup,
        results,
      });

      toastMessage(
        "success",
        language[this.props.defaultLanguage].emergency_post_release_results
      );

      this.setState({ isSubmitting: false, rows: {} });

      if (this.props.onSaved) this.props.onSaved();
    } catch (error) {
      this.setState({ isSubmitting: false });
      toastMessage("error", error);
    }
  };

  onRecall = async () => {
    const unit = this.state.recalling;

    if (!unit) return;

    try {
      this.setState({ isRecalling: true });

      await recallEmergencyUnit({
        id: this.props.request._id,
        donationNumber: unit.donationNumber,
        code: unit.code,
      });

      toastMessage(
        "success",
        language[this.props.defaultLanguage].emergency_recalled
      );

      this.setState({ isRecalling: false, recalling: null });

      this.getIssued();

      if (this.props.onSaved) this.props.onSaved();
    } catch (error) {
      this.setState({ isRecalling: false });
      toastMessage("error", error);
    }
  };

  renderRow = (unit) => {
    const { defaultLanguage } = this.props;
    const donationNumber = unit?.donation?.donationNumber || "";
    const code = unit?.inventory?.code || "";
    const result = this.recorded()[donationNumber];
    const row = this.state.rows[donationNumber] || {};

    if (result) {
      const incompatible =
        String(result.compatibility).toLowerCase() === "incompatible";

      return (
        <tr key={donationNumber} className={incompatible ? "text-error" : ""}>
          <td>{donationNumber}</td>
          <td>{result.bloodGroup}</td>
          <td>{result.immediateSpin}</td>
          <td>{result.indirectCoombs}</td>
          <td>{result.checkCells}</td>
          <td>
            {incompatible
              ? language[defaultLanguage].incompatible
              : language[defaultLanguage].compatible}
          </td>
          <td>{result.comments}</td>
          <td>
            {result.recalledAt ? (
              <span className="text-muted">
                {language[defaultLanguage].emergency_recalled} -{" "}
                {moment(result.recalledAt).format("lll")}
              </span>
            ) : (
              incompatible &&
              this.state.canRecord && (
                <Button
                  className="btn-sm btn-danger"
                  text={language[defaultLanguage].emergency_recall_unit}
                  onPress={() =>
                    this.setState({ recalling: { donationNumber, code } })
                  }
                />
              )
            )}
          </td>
        </tr>
      );
    }

    return (
      <tr key={donationNumber}>
        <td>{donationNumber}</td>
        <td>
          <Input
            value={row.bloodGroup ?? unit?.inventory?.bloodGroup ?? ""}
            onChange={(e) =>
              this.setCell(donationNumber, "bloodGroup", e.target.value)
            }
          />
        </td>
        <td>
          <Select
            options={REACTIVITY}
            value={
              REACTIVITY.find((el) => el.value === row.immediateSpin) || null
            }
            onChange={(e) =>
              this.setCell(donationNumber, "immediateSpin", e?.value || "")
            }
          />
        </td>
        <td>
          <Select
            options={REACTIVITY}
            value={
              REACTIVITY.find((el) => el.value === row.indirectCoombs) || null
            }
            onChange={(e) =>
              this.setCell(donationNumber, "indirectCoombs", e?.value || "")
            }
          />
        </td>
        <td>
          <Select
            options={CHECK_CELLS}
            value={
              CHECK_CELLS.find((el) => el.value === row.checkCells) || null
            }
            onChange={(e) =>
              this.setCell(donationNumber, "checkCells", e?.value || "")
            }
          />
        </td>
        <td>
          <Select
            options={COMPATIBILITY}
            value={
              COMPATIBILITY.find((el) => el.value === row.compatibility) || null
            }
            onChange={(e) =>
              this.setCell(donationNumber, "compatibility", e?.value || "")
            }
          />
        </td>
        <td colSpan={2}>
          <Input
            value={row.comments || ""}
            onChange={(e) =>
              this.setCell(donationNumber, "comments", e.target.value)
            }
          />
        </td>
      </tr>
    );
  };

  render() {
    const { defaultLanguage } = this.props;
    const { issued } = this.state;
    const pending = issued.filter(
      (el) => !this.recorded()[el?.donation?.donationNumber]
    );

    return (
      <>
        <Separator
          title={language[defaultLanguage].emergency_post_release_results}
          titleClassName="text-primary"
        />

        {!issued.length ? (
          <div className="col-md-12">
            <p className="text-muted">
              {language[defaultLanguage].emergency_products_issued}: 0
            </p>
          </div>
        ) : (
          <div className="col-md-12">
            <div className="col-md-4 mb-3">
              <Input
                label={language[defaultLanguage].patient_blood_group}
                value={this.state.patientBloodGroup}
                onChange={(e) =>
                  this.setState({ patientBloodGroup: e.target.value })
                }
                disabled={!this.state.canRecord}
              />
            </div>

            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>{language[defaultLanguage].donation_number}</th>
                    <th>{language[defaultLanguage].blood_group}</th>
                    <th>{language[defaultLanguage].immediate_spin}</th>
                    <th>{language[defaultLanguage].indirect_coombs}</th>
                    <th>{language[defaultLanguage].check_cells}</th>
                    <th>{language[defaultLanguage].compatibility}</th>
                    <th colSpan={2}>
                      {language[defaultLanguage].comments}
                    </th>
                  </tr>
                </thead>
                <tbody>{issued.map(this.renderRow)}</tbody>
              </table>
            </div>

            {this.state.canRecord && pending.length > 0 && (
              <Button
                text={language[defaultLanguage].save}
                onPress={this.onSave}
                isSubmitting={this.state.isSubmitting}
              />
            )}
          </div>
        )}

        <Modal
          show={!!this.state.recalling}
          handleClose={() => this.setState({ recalling: null })}
          title={language[defaultLanguage].emergency_recall_unit}
          showHeaderBottomBorder={false}
        >
          <ConfirmationModal
            error={{}}
            withReason={false}
            title={language[defaultLanguage].emergency_recall_unit}
            description={
              language[defaultLanguage].emergency_incompatible_warning
            }
            negativeButtonText={language[defaultLanguage].cancel}
            positiveButtonText={language[defaultLanguage].emergency_recall_unit}
            onPressNegative={() => this.setState({ recalling: null })}
            onPressPositive={this.onRecall}
            isPositiveSubmitting={this.state.isRecalling}
          />
        </Modal>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;

  return { defaultLanguage };
};

export default connect(mapStateToProps)(PostReleaseResults);
